import { qoursesApi } from '@/api/qourses.tsx'
import { CourseGroupName } from '@/components/Resolvers.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import useGetCourseGroup, {
  getCourseGroupQueryKey,
} from '@/hooks/courseGroups/useGetCourseGroup.tsx'
import { getCourseGroupsQueryKey } from '@/hooks/courses/useGetCourseGroups.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { ToastVariant, minDelay, sendGenericErrorNotification, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { Archive, GroupIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function ArchiveCourseGroupModal({ courseGroupId }: { courseGroupId: string }) {
  const { courseGroup, isLoading, isError } = useGetCourseGroup(courseGroupId)
  const [isDeleting, setIsDeleting] = useState(false)

  const queryClient = useQueryClient()

  const { t: translate } = useTranslation()

  const handleArchiveCourseGroup = async () => {
    try {
      setIsDeleting(true)
      await minDelay(
        qoursesApi.courseGroup.courseGroupControllerArchiveCourseGroupV1(courseGroup.id),
        1000,
      )
      sendNotification(
        translate('modals.archiveCourseGroupModal.notification.title'),
        translate('modals.archiveCourseGroupModal.notification.subtitle'),
        ToastVariant.Success,
      )
      popAllModals()
      queryClient.invalidateQueries(getCourseGroupsQueryKey({}))
      queryClient.invalidateQueries(getCourseGroupQueryKey(courseGroupId))
    } catch (e) {
      console.error(e)
      sendGenericErrorNotification()
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('common.loading')}</DialogTitle>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  if (isError) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('modals.archiveCourseGroupModal.errorState.title')}</DialogTitle>
          <DialogDescription>
            {translate('modals.archiveCourseGroupModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogHeader className="mb-2">
          <DialogTitle className="mt-6 sm:mt-2"></DialogTitle>
          <DialogTitle className="mt-6 flex flex-col gap-y-2 sm:mt-2">
            <div className="flex flex-row items-center justify-center text-sm text-muted-foreground sm:justify-start">
              <GroupIcon className="mr-1 size-4 text-muted-foreground" />
              <CourseGroupName courseGroupId={courseGroupId} />
            </div>
            <p>{translate('modals.archiveCourseGroupModal.title')}</p>
          </DialogTitle>
          <DialogDescription>
            {translate('modals.archiveCourseGroupModal.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </DialogHeader>
      <Button
        variant="destructive"
        className="bezel"
        disabled={isDeleting}
        onClick={handleArchiveCourseGroup}
      >
        {isDeleting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {translate('common.loading')}
          </>
        ) : (
          <>
            <Archive className="mr-2 h-4 w-4" />
            {translate('modals.archiveCourseGroupModal.button')}
          </>
        )}
      </Button>
    </Dynamic.Content>
  )
}
